<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ReactionPicker from './ReactionPicker.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import MessageFormatter from 'shared/helpers/MessageFormatter';
import { formatTime, quoteText, senderName } from '../helpers/timeline';

const props = defineProps({
  message: { type: Object, required: true },
  continued: { type: Boolean, default: false },
  canPin: { type: Boolean, default: false },
  status: { type: String, default: null },
  // Lists in the side panel: no hover actions, tighter spacing.
  compact: { type: Boolean, default: false },
  currentUserId: { type: Number, default: null },
  // Name of a user id, for "who reacted".
  nameFor: { type: Function, default: () => '' },
  // Flashes when the reader jumps here from a quote.
  highlighted: { type: Boolean, default: false },
  // Read-only channels: no "Reply" for people who cannot post.
  canReply: { type: Boolean, default: true },
  // How far each person got in the channel ({ [userId]: receipt }), for the
  // ticks on the current user's own messages.
  receipts: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['star', 'pin', 'react', 'reply', 'jumpTo', 'info']);


// One click away on hover; anything else through the full picker.
const QUICK_REACTIONS = ['👍', '❤️', '😂'];

const { t, locale } = useI18n();

const name = computed(() => senderName(props.message));
const time = computed(() => formatTime(props.message.created_at, locale.value));
const html = computed(() =>
  props.message.content
    ? new MessageFormatter(props.message.content).formattedMessage
    : ''
);
const images = computed(() =>
  (props.message.attachments || []).filter(attachment => attachment.is_image)
);
const files = computed(() =>
  (props.message.attachments || []).filter(attachment => !attachment.is_image)
);
const quote = computed(() => {
  const original = props.message.reply_to;
  if (!original) return null;
  const text = quoteText(original.content);
  return {
    id: original.id,
    name: original.user?.available_name || original.user?.name || '',
    text: text || (original.has_attachments ? `📎 ${t('TEAM_CHAT.ATTACHMENT')}` : ''),
  };
});

const reactions = computed(() => props.message.reactions || []);
const reactedByMe = reaction => reaction.user_ids.includes(props.currentUserId);
const reactorNames = reaction =>
  reaction.user_ids
    .map(id =>
      id === props.currentUserId ? t('TEAM_CHAT.YOU') : props.nameFor(id)
    )
    .filter(Boolean)
    .join(', ');

const react = emoji => emit('react', props.message, emoji);

// WhatsApp-style ticks: sent, delivered to everyone, read by everyone.
const TICKS = {
  sent: { icon: 'i-lucide-check', class: 'text-n-slate-10' },
  delivered: { icon: 'i-lucide-check-check', class: 'text-n-slate-10' },
  read: { icon: 'i-lucide-check-check', class: 'text-n-blue-10' },
};

const tick = computed(() => {
  if (props.compact || props.message.user_id !== props.currentUserId) return null;
  const recipients = Object.values(props.receipts).filter(
    receipt => receipt.user_id !== props.message.user_id
  );
  if (!recipients.length) return null;
  const at = props.message.created_at;
  if (recipients.every(receipt => (receipt.last_read_at || 0) >= at)) {
    return 'read';
  }
  const reached = receipt =>
    Math.max(receipt.last_read_at || 0, receipt.last_delivered_at || 0) >= at;
  return recipients.every(reached) ? 'delivered' : 'sent';
});

const extension = filename =>
  filename.includes('.') ? filename.split('.').pop().toUpperCase() : '';
</script>

<template>
  <div
    class="relative flex gap-3 px-2 -mx-2 transition-colors duration-700 rounded-lg group"
    :class="[
      continued ? 'py-0.5' : 'pt-2 pb-0.5',
      compact ? '' : 'hover:bg-n-alpha-1',
      message.pinned && !compact ? 'bg-n-alpha-1' : '',
      highlighted ? '!bg-n-amber-3' : '',
    ]"
  >
    <div class="flex justify-center flex-shrink-0 w-9">
      <Avatar
        v-if="!continued"
        :src="message.user?.thumbnail"
        :name="name || '?'"
        :status="status"
        :size="36"
        rounded-full
        hide-offline-status
      />
      <span
        v-else-if="message.pinned"
        class="mt-1 size-3 i-lucide-pin text-n-brand"
        :title="t('TEAM_CHAT.PINNED')"
      />
      <span
        v-else
        class="mt-1 text-[0.625rem] tabular-nums opacity-0 text-n-slate-10 group-hover:opacity-100"
      >
        {{ time }}
      </span>
    </div>

    <div class="flex-1 min-w-0">
      <div v-if="!continued" class="flex items-baseline gap-2">
        <span class="text-sm font-semibold text-n-slate-12">{{ name }}</span>
        <span class="text-xs tabular-nums text-n-slate-10">{{ time }}</span>
        <span
          v-if="message.pinned"
          class="self-center size-3 i-lucide-pin text-n-brand"
          :title="t('TEAM_CHAT.PINNED')"
        />
        <span
          v-if="message.starred"
          class="self-center size-3 i-lucide-star text-n-amber-9"
          :title="t('TEAM_CHAT.STARRED')"
        />
      </div>
      <button
        v-if="quote"
        type="button"
        class="relative flex flex-col w-full max-w-md py-1 my-1 overflow-hidden text-xs rounded-md ps-3.5 pe-2.5 text-start bg-n-alpha-1 hover:bg-n-alpha-2"
        :disabled="compact"
        @click="emit('jumpTo', quote.id)"
      >
        <!-- A separate bar: button resets wipe borders. -->
        <span class="absolute inset-y-0 start-0 w-[3px] bg-n-brand" />
        <span class="font-semibold text-n-slate-12">{{ quote.name }}</span>
        <span class="truncate text-n-slate-11">{{ quote.text }}</span>
      </button>
      <div
        v-if="html"
        v-dompurify-html="html"
        class="text-sm leading-relaxed break-words text-n-slate-12 [&_p]:mb-0 [&_.prosemirror-mention-node]:font-medium [&_.prosemirror-mention-node]:text-n-brand [&_a]:text-n-brand [&_a]:underline"
      />
      <div v-if="images.length" class="flex flex-wrap gap-2 mt-1.5">
        <a
          v-for="image in images"
          :key="image.id"
          :href="image.url"
          target="_blank"
          rel="noopener noreferrer"
          class="block overflow-hidden border rounded-xl border-n-weak"
        >
          <img
            :src="image.url"
            :alt="image.filename"
            class="object-cover max-w-xs max-h-60"
            :class="compact ? 'max-h-32' : ''"
          />
        </a>
      </div>
      <div v-if="files.length" class="flex flex-wrap gap-2 mt-1.5">
        <a
          v-for="file in files"
          :key="file.id"
          :href="file.url"
          target="_blank"
          rel="noopener noreferrer"
          class="flex items-center gap-3 py-2 pl-2 pr-4 border rounded-xl w-72 max-w-full border-n-weak hover:bg-n-alpha-1"
        >
          <span
            class="flex items-center justify-center flex-shrink-0 rounded-lg size-9 bg-n-ruby-3 text-n-ruby-11"
          >
            <span class="size-4 i-lucide-file-text" />
          </span>
          <span class="min-w-0">
            <span class="block text-sm font-medium truncate text-n-slate-12">
              {{ file.filename }}
            </span>
            <span class="block text-xs text-n-slate-10">
              {{ extension(file.filename) }}
            </span>
          </span>
        </a>
      </div>
      <div
        v-if="reactions.length"
        class="flex flex-wrap items-center gap-1.5 mt-1.5"
      >
        <button
          v-for="reaction in reactions"
          :key="reaction.emoji"
          type="button"
          class="flex items-center h-6 gap-1 px-2 text-xs transition-colors border rounded-full tabular-nums"
          :class="
            reactedByMe(reaction)
              ? 'border-n-brand/40 bg-n-brand/10 font-semibold text-n-slate-12'
              : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2'
          "
          :disabled="compact"
          :title="reactorNames(reaction)"
          @click="react(reaction.emoji)"
        >
          <span class="text-sm leading-none">{{ reaction.emoji }}</span>
          {{ reaction.user_ids.length }}
        </button>
        <ReactionPicker v-if="!compact" @select="react">
          <button
            type="button"
            class="flex items-center justify-center h-6 px-1.5 transition-opacity border border-transparent rounded-full opacity-0 text-n-slate-10 group-hover:opacity-100 hover:bg-n-alpha-2 hover:border-n-weak"
            :title="t('TEAM_CHAT.ADD_REACTION')"
          >
            <span class="size-4 i-lucide-smile-plus" />
          </button>
        </ReactionPicker>
      </div>
    </div>

    <button
      v-if="tick"
      type="button"
      class="self-end mb-1 shrink-0"
      :class="TICKS[tick].class"
      :title="t(`TEAM_CHAT.RECEIPTS.${tick.toUpperCase()}`)"
      @click="emit('info', message)"
    >
      <span class="block size-4" :class="TICKS[tick].icon" />
    </button>

    <div
      v-if="!compact"
      class="absolute flex items-center gap-0.5 p-1 transition-opacity border rounded-xl shadow-sm opacity-0 -top-5 end-3 bg-n-solid-1 border-n-weak group-hover:opacity-100 focus-within:opacity-100"
    >
      <template v-if="canReply">
        <button
          type="button"
          class="flex items-center justify-center rounded-lg size-9 text-n-slate-11 hover:bg-n-alpha-2"
          :title="t('TEAM_CHAT.REPLY')"
          @click="emit('reply', message)"
        >
          <span class="size-5 i-lucide-reply" />
        </button>
        <span class="w-px h-5 mx-0.5 bg-n-weak" />
      </template>
      <button
        v-for="emoji in QUICK_REACTIONS"
        :key="emoji"
        type="button"
        class="flex items-center justify-center text-xl leading-none rounded-lg size-9 hover:bg-n-alpha-2"
        :title="t('TEAM_CHAT.REACT_WITH', { emoji })"
        @click="react(emoji)"
      >
        {{ emoji }}
      </button>
      <ReactionPicker @select="react">
        <button
          type="button"
          class="flex items-center justify-center rounded-lg size-9 text-n-slate-11 hover:bg-n-alpha-2"
          :title="t('TEAM_CHAT.ADD_REACTION')"
        >
          <span class="size-5 i-lucide-smile-plus" />
        </button>
      </ReactionPicker>
      <span class="w-px h-5 mx-0.5 bg-n-weak" />
      <button
        type="button"
        class="flex items-center justify-center rounded-lg size-9 hover:bg-n-alpha-2"
        :class="message.starred ? 'text-n-amber-9' : 'text-n-slate-11'"
        :title="message.starred ? t('TEAM_CHAT.UNSTAR') : t('TEAM_CHAT.STAR')"
        @click="emit('star', message)"
      >
        <span class="size-5 i-lucide-star" />
      </button>
      <button
        v-if="canPin"
        type="button"
        class="flex items-center justify-center rounded-lg size-9 hover:bg-n-alpha-2"
        :class="message.pinned ? 'text-n-brand' : 'text-n-slate-11'"
        :title="message.pinned ? t('TEAM_CHAT.UNPIN') : t('TEAM_CHAT.PIN')"
        @click="emit('pin', message)"
      >
        <span class="size-5 i-lucide-pin" />
      </button>
      <button
        v-if="tick"
        type="button"
        class="flex items-center justify-center rounded-lg size-9 text-n-slate-11 hover:bg-n-alpha-2"
        :title="t('TEAM_CHAT.RECEIPTS.INFO_TITLE')"
        @click="emit('info', message)"
      >
        <span class="size-5 i-lucide-info" />
      </button>
    </div>
  </div>
</template>
